import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { getFresh } from "../common/apiCache";
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip as ReTooltip, Legend, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { Plus, X, Trophy, SlidersHorizontal, RotateCcw, ChevronLeft, ChevronRight } from "lucide-react";
import Loader from "../common/Loader";
import MultiSelectDropdown from "../common/MultiSelectDropdown";
import { displayMessage } from "../../Utils/helper";

const REACT_APP_API_BASE_URL = process.env.REACT_APP_API_BASE_URL

// Backend only accepts one scalar value per filter (strict `=`), so selecting
// several values means fanning out one request per combination and merging
// the results client-side. Cap it so a careless "select all" can't fire
// hundreds of requests at once.
const MAX_COMBOS = 60;

// ── App palette (same as Report4) ────────────────────────────────────────────
const PALETTE = [
    "#032B4E", "#2EB8AA", "#F2994A", "#7a35c8", "#e74c3c",
    "#3498db", "#27ae60", "#f39c12", "#9b59b6", "#1abc9c",
    "#e67e22", "#34495e", "#2ecc71", "#8e44ad", "#2980b9",
];

// Client needs the exact underlying number, not a rounded approximation — so every
// displayed value is TRUNCATED (chopped toward zero) to 2 decimal places, never rounded.
const truncateDp = (v, dp = 2) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return null;
    const factor = 10 ** dp;
    return Math.trunc(n * factor) / factor;
};
const fmtNum = (v) => {
    const t = truncateDp(v, 2);
    return t === null ? "-" : t.toFixed(2);
};

const solo = (arr) => (arr.length === 1 ? arr[0] : undefined);
const summarize = (arr) => (arr.length === 0 ? null : arr.length === 1 ? arr[0] : `${arr.length} selected`);

// Cartesian product of { key: [values...] } → [{ key: value, ... }, ...]
const cartesian = (arrsObj) =>
    Object.entries(arrsObj).reduce(
        (acc, [key, arr]) => acc.flatMap((combo) => arr.map((v) => ({ ...combo, [key]: v }))),
        [{}]
    );

// ── Shared style tokens ───────────────────────────────────────────────────────
// Every header (section titles, the "Dynamic Filters" sidebar title) shares one
// size/style, and every filter label shares another — identical between
// Conjoint / New Product Development and Trend Analysis.
const ST = {
    secTitle: { fontSize: 14, fontWeight: 700, color: "#0c3c54", fontFamily: "Poppins, sans-serif", marginBottom: 10 },
    bigTitle: { fontSize: 14, fontWeight: 700, color: "#0c3c54", fontFamily: "Poppins, sans-serif" },
    subtitle: { fontSize: 13, color: "#6a7d8c", fontFamily: "Poppins, sans-serif" },
    card: {
        background: "#fff", border: "1px solid #e6eef4", borderRadius: 12,
        boxShadow: "0 3px 12px rgba(12, 60, 84, 0.06)",
    },
    label: {
        display: "block", fontSize: 13, fontWeight: 600, color: "#0c3c54",
        fontFamily: "Poppins, sans-serif", marginBottom: 6, marginTop: 14,
    },
    select: {
        width: "100%", padding: "9px 12px", borderRadius: 6, border: "1px solid #d1d5db",
        outline: "none", fontSize: 13, background: "#fff", color: "#374151", boxSizing: "border-box",
    },
    wrap: { background: "#fff", border: "1px solid #d9d9d9", borderRadius: "6px 6px 0 0", overflowX: "auto" },
    th: {
        position: "sticky", top: 0, zIndex: 10, background: "#022646", color: "#fff",
        textAlign: "left", padding: "10px 12px", whiteSpace: "nowrap", borderBottom: "1px solid #d9d9d9",
        fontSize: 13,
    },
    center: {
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "60px 20px", textAlign: "center", minHeight: 300,
    },
};

// ── Chart tooltips ───────────────────────────────────────────────────────────
function PieTooltipBox({ active, payload }) {
    if (!active || !payload?.length) return null;
    const d = payload[0];
    return (
        <div style={{ background: "#fff", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 150 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                <span style={{ width: 12, height: 12, borderRadius: "50%", background: d.payload.fill, flexShrink: 0 }} />
                <strong style={{ color: "#1e293b" }}>{d.name}</strong>
            </div>
            <div style={{ color: "#374151" }}>Importance: <strong>{fmtNum(d.value)}%</strong></div>
        </div>
    );
}

function BarTooltipBox({ active, payload, label }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{ background: "#fff", padding: "8px 12px", border: "1px solid #e5e7eb", borderRadius: 6, fontSize: 13, boxShadow: "0 4px 12px rgba(0,0,0,0.12)", minWidth: 150 }}>
            <strong style={{ color: "#1e293b" }}>{label}</strong>
            <div style={{ color: "#374151", marginTop: 4 }}>
                Part-Worth: <strong>{fmtNum(payload[0].value)}</strong>
            </div>
        </div>
    );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function ConjointDashboard({ embedded = false } = {}) {
    // Filter options
    const [options, setOptions] = useState({
        datasets: [], region_levels: [], financial_years: [],
        zones: [], segments: [], product_categories: [],
    });

    // Selected filters — each is now an array so users can pick one, many, or all.
    const [dataset, setDataset] = useState([]);
    const [regionLevel, setRegionLevel] = useState([]);
    const [financialYear, setFinancialYear] = useState([]);
    const [zone, setZone] = useState([]);
    const [segment, setSegment] = useState([]);
    const [category, setCategory] = useState([]);

    const isZonal = regionLevel.some((r) => r?.toLowerCase() === "zonal");

    // Filter options loading — true on the very first load AND on every cascade refetch,
    // so users can't pick from a stale/empty options list mid-fetch.
    const [filtersLoading, setFiltersLoading] = useState(true);
    const [filtersLoadedOnce, setFiltersLoadedOnce] = useState(false);

    // Filters are hierarchical (each selection narrows the options below it).
    // Changing an upstream filter must clear everything downstream — otherwise
    // stale downstream values get sent as params on the next fetch and the user
    // gets stuck with no way back to a working state short of a page refresh.
    const FILTER_ORDER = ["dataset", "regionLevel", "financialYear", "zone", "segment", "category"];
    const filterSetters = {
        dataset: setDataset, regionLevel: setRegionLevel, financialYear: setFinancialYear,
        zone: setZone, segment: setSegment, category: setCategory,
    };
    const handleFilterChange = (key, values) => {
        filterSetters[key](values);
        const idx = FILTER_ORDER.indexOf(key);
        FILTER_ORDER.slice(idx + 1).forEach((k) => filterSetters[k]([]));
    };
    const resetAllFilters = () => {
        setDataset([]);
        setRegionLevel([]);
        setZone([]);
        setSegment([]);
        setCategory([]);
        // financialYear is re-derived automatically once options refresh
    };

    // Dashboard data (merged across every selected filter combination)
    const [importance, setImportance] = useState([]);
    const [partWorths, setPartWorths] = useState([]);
    const [dashLoading, setDashLoading] = useState(false);
    const [dashError, setDashError] = useState(null);
    const [comboCount, setComboCount] = useState(0);

    // UI
    const [selectedChartFeature, setSelectedChartFeature] = useState("");

    // Simulator — dynamic product list (add/remove as many as needed)
    const [products, setProducts] = useState([]);
    const [filtersOpen, setFiltersOpen] = useState(true);
    const productCounterRef = useRef(0);

    const nextProductName = () => {
        productCounterRef.current += 1;
        const n = productCounterRef.current;
        return n <= 26 ? `Product ${String.fromCharCode(64 + n)}` : `Product ${n}`;
    };

    // ── Fetch filter options ──────────────────────────────────────────────────
    // Only narrows on an upstream filter when exactly one value is selected there —
    // with 0 or 2+ selected, the backend can't express "any of these", so we
    // simply don't restrict on that field rather than guessing with one value.
    useEffect(() => {
        let active = true;
        const loadingTimer = setTimeout(() => {
            if (active) setFiltersLoading(true);
        }, 100);

        (async () => {
            try {
                const params = {};
                const d = solo(dataset); if (d) params.dataset = d;
                const rl = solo(regionLevel); if (rl) params.region_level = rl;
                // financial_year is intentionally NOT sent here: it's a value derived
                // FROM this very response (locked to the latest year available), not
                // a real upstream filter. Sending it back would self-constrain every
                // other cascading list to whatever year happened to be locked before —
                // e.g. switching Geographic Granularity would keep filtering by the
                // now-stale year and could return zero years/zones for the new scope.
                const z = solo(zone); if (z) params.zone = z;
                const sg = solo(segment); if (sg) params.segment = sg;
                // Product Category is sent so `financial_years` (and the derived
                // "Latest" FY) is scoped to the FULL selection. Without it, FY locks
                // to the global latest year (a partial year that's missing many
                // segment/category combos), and those combos render empty even
                // though earlier years have data. See the FY reconciliation effect.
                const cat = solo(category); if (cat) params.product_category = cat;
                const res = await getFresh(`${REACT_APP_API_BASE_URL}/conjoint/filters`, { params });
                if (active && res.data?.status === "success") setOptions(res.data.data);
            } catch (e) {
                console.error("Failed to fetch conjoint filters", e);
            } finally {
                clearTimeout(loadingTimer);
                if (active) { setFiltersLoading(false); setFiltersLoadedOnce(true); }
            }
        })();
        return () => { active = false; };
    }, [dataset, regionLevel, zone, segment, category]);

    // Financial Year is locked to the latest available year for the current
    // cascade (Product Type / Geographic Granularity) — never user-editable.
    // Backend returns years pre-sorted ascending, so the last entry is latest.
    const latestFinancialYear = options.financial_years?.length
        ? options.financial_years[options.financial_years.length - 1]
        : "";

    // Financial Year is derived state, not user-editable, so it must always be
    // reconciled to `latestFinancialYear`. This has to depend on `financialYear`
    // too — otherwise an upstream filter change that CLEARS financialYear (via
    // handleFilterChange) but leaves the latest-year STRING unchanged (e.g.
    // switching Faucet ↔ Sanitaryware / National ↔ Zonal when both share the same
    // latest year) would never re-run this effect, leaving FY permanently empty →
    // zero filter combinations → the dashboard fetch silently never fires.
    useEffect(() => {
        const desired = latestFinancialYear ? [latestFinancialYear] : [];
        const isSame =
            desired.length === financialYear.length &&
            desired.every((v, i) => v === financialYear[i]);
        if (!isSame) setFinancialYear(desired);
        // Setting `desired` makes `isSame` true on the next run, so this can't loop.
    }, [latestFinancialYear, financialYear]);

    // Auto-select defaults for all main filters so the dashboard isn't empty on load
    useEffect(() => {
        if (!dataset.length && options.datasets?.length) setDataset([options.datasets[0]]);
    }, [dataset, options.datasets]);

    useEffect(() => {
        if (!regionLevel.length && options.region_levels?.length) setRegionLevel([options.region_levels[0]]);
    }, [regionLevel, options.region_levels]);

    useEffect(() => {
        if (!segment.length && options.segments?.length) setSegment([options.segments[0]]);
    }, [segment, options.segments]);

    useEffect(() => {
        if (!category.length && options.product_categories?.length) setCategory([options.product_categories[0]]);
    }, [category, options.product_categories]);

    useEffect(() => {
        if (!isZonal) {
            if (zone.length) setZone([]);
            return;
        }
        const zoneOptions = options.zones || [];
        if (zoneOptions.length && !zoneOptions.includes(zone[0])) {
            setZone([zoneOptions[0]]);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isZonal, options.zones]);

    // ── Build every valid filter combination to fetch ─────────────────────────
    const buildCombos = useCallback(() => {
        const base = cartesian({ dataset, regionLevel, financialYear, segment, category });
        const combos = [];
        base.forEach((combo) => {
            if (combo.regionLevel?.toLowerCase() === "zonal") {
                if (!zone.length) return; // incomplete — zone required but not chosen yet
                zone.forEach((z) => combos.push({ ...combo, zone: z }));
            } else {
                combos.push(combo);
            }
        });
        return combos;
    }, [dataset, regionLevel, financialYear, zone, segment, category]);

    // ── Fetch dashboard data for every combo and merge ────────────────────────
    useEffect(() => {
        const combos = buildCombos();

        if (!combos.length) {
            setImportance([]); setPartWorths([]); setDashError(null); setComboCount(0);
            return;
        }
        if (combos.length > MAX_COMBOS) {
            setImportance([]); setPartWorths([]); setComboCount(0);
            setDashError(`Too many filter combinations selected (${combos.length}). Please narrow your selection to ${MAX_COMBOS} or fewer.`);
            return;
        }

        let active = true;
        setDashError(null);
        const dashLoadingTimer = setTimeout(() => {
            if (active) setDashLoading(true);
        }, 150);

        (async () => {
            try {
                const settled = await Promise.allSettled(
                    combos.map((combo) => getFresh(`${REACT_APP_API_BASE_URL}/conjoint/dashboard-data`, {
                        params: {
                            dataset: combo.dataset, region_level: combo.regionLevel,
                            financial_year: combo.financialYear, segment: combo.segment,
                            product_category: combo.category,
                            ...(combo.zone ? { zone: combo.zone } : {}),
                        },
                    }))
                );
                clearTimeout(dashLoadingTimer);
                if (!active) return;
                setDashLoading(false);

                const succeeded = settled.filter((r) => r.status === "fulfilled" && r.value.data?.status === "success");
                if (!succeeded.length) {
                    setImportance([]); setPartWorths([]); setComboCount(0);
                    setDashError("No data returned for this selection.");
                    return;
                }

                // Merge by averaging across combos — each combo represents an
                // independently fitted utility model, so an unweighted mean is the
                // most defensible way to show a "combined" view across selections.
                const impAgg = new Map();
                const pwAgg = new Map();
                succeeded.forEach((r) => {
                    (r.value.data.data.importance || []).forEach((row) => {
                        const cur = impAgg.get(row.feature) || { sum: 0, count: 0 };
                        cur.sum += Number(row.relative_importance_pct) || 0;
                        cur.count += 1;
                        impAgg.set(row.feature, cur);
                    });
                    (r.value.data.data.part_worths || []).forEach((row) => {
                        const key = `${row.feature}||${row.feature_level}`;
                        const cur = pwAgg.get(key) || { feature: row.feature, feature_level: row.feature_level, sum: 0, count: 0 };
                        cur.sum += Number(row.part_worth) || 0;
                        cur.count += 1;
                        pwAgg.set(key, cur);
                    });
                });

                const mergedImportance = Array.from(impAgg.entries()).map(([feature, { sum, count }]) => ({
                    feature, relative_importance_pct: sum / count,
                }));
                const mergedPartWorths = Array.from(pwAgg.values()).map(({ feature, feature_level, sum, count }) => ({
                    feature, feature_level, part_worth: sum / count,
                }));

                setImportance(mergedImportance);
                setPartWorths(mergedPartWorths);
                setComboCount(combos.length);
                if (mergedImportance.length > 0) setSelectedChartFeature(mergedImportance[0].feature);
            } catch (e) {
                if (active) {
                    setImportance([]); setPartWorths([]); setComboCount(0);
                    setDashError("An error occurred while fetching analysis data.");
                }
            } finally {
                if (active) setDashLoading(false);
            }
        })();
        return () => { active = false; };
    }, [buildCombos]);

    // ── Derived data ──────────────────────────────────────────────────────────
    const allFeatures = useMemo(() => [...new Set(partWorths.map((pw) => pw.feature))], [partWorths]);

    const featureLevels = useMemo(() => {
        const map = {};
        partWorths.forEach((pw) => {
            if (!map[pw.feature]) map[pw.feature] = [];
            if (!map[pw.feature].includes(pw.feature_level)) map[pw.feature].push(pw.feature_level);
        });
        return map;
    }, [partWorths]);

    const pwLookup = useMemo(() => {
        const m = new Map();
        partWorths.forEach((pw) => m.set(`${pw.feature}||${pw.feature_level}`, Number(pw.part_worth) || 0));
        return m;
    }, [partWorths]);

    const calcUtility = (config) =>
        Object.entries(config || {}).reduce((sum, [f, lvl]) => sum + (pwLookup.get(`${f}||${lvl}`) || 0), 0);

    const bestFeature = useMemo(() => {
        if (!importance.length) return null;
        return [...importance].sort((a, b) => Number(b.relative_importance_pct) - Number(a.relative_importance_pct))[0];
    }, [importance]);

    const bestConfig = useMemo(() => {
        const config = {};
        allFeatures.forEach((f) => {
            const levels = partWorths.filter((pw) => pw.feature === f);
            const best = [...levels].sort((a, b) => Number(b.part_worth) - Number(a.part_worth))[0];
            if (best) config[f] = best.feature_level;
        });
        return config;
    }, [allFeatures, partWorths]);

    // Only the fixed "Optimal Product" reference card is shown by default — no
    // comparison product exists until the user clicks Add Product. This effect
    // just clears any previously-added products whenever the underlying data
    // reloads (filters changed), which also fixes the Utility-score reactivity
    // bug: a previously-added product could otherwise keep holding a
    // feature-level value that no longer exists in the new part-worth data —
    // calcUtility would then silently score that feature as 0 instead of
    // visibly reflecting the change.
    useEffect(() => {
        setProducts([]);
        productCounterRef.current = 0;
    }, [partWorths]);

    const pieData = useMemo(
        () => importance.map((imp) => ({
            name: imp.feature,
            value: truncateDp(imp.relative_importance_pct) ?? 0,
        })),
        [importance]
    );

    const barData = useMemo(() => {
        if (!selectedChartFeature) return [];
        return partWorths
            .filter((pw) => pw.feature === selectedChartFeature)
            .map((pw) => ({ name: pw.feature_level, partWorth: truncateDp(pw.part_worth) ?? 0 }))
            .sort((a, b) => b.partWorth - a.partWorth);
    }, [partWorths, selectedChartFeature]);

    // ── Simulator ranking (softmax preference share) ──────────────────────────
    const ranking = useMemo(() => {
        if (!partWorths.length) return [];
        const rows = [
            { name: "Optimal Product", config: bestConfig, isOptimal: true },
            ...products.map((p) => ({ name: p.name, config: p.config, isOptimal: false })),
        ].map((p) => {
            const utility = calcUtility(p.config);
            return { ...p, utility, exp: Math.exp(utility) };
        });
        const sumExp = rows.reduce((s, r) => s + r.exp, 0);
        return rows
            .map((r) => ({ ...r, share: sumExp > 0 ? (r.exp / sumExp) * 100 : 0 }))
            .sort((a, b) => b.utility - a.utility);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [products, bestConfig, pwLookup]);

    // ── Simulator handlers ────────────────────────────────────────────────────
    // A newly added product must start identical to the Optimal Product (same
    // filters, same Utility) so the user is comparing a deliberate edit, not a
    // fresh guess. The first product added this way becomes "Product A".
    const addProduct = () => {
        if (products.length >= 5) {
            displayMessage("warning", "Limit Reached", "You can create a maximum of 5 products for comparison.");
            return;
        }
        setProducts((prev) => [...prev, { id: Date.now(), name: nextProductName(), config: { ...bestConfig } }]);
    };
    const removeProduct = (id) => setProducts((prev) => prev.filter((p) => p.id !== id));
    const updateProduct = (id, feature, level) =>
        setProducts((prev) => prev.map((p) => (p.id === id ? { ...p, config: { ...p.config, [feature]: level } } : p)));

    const filtersComplete = importance.length > 0;
    const datasetLabel = summarize(dataset);

    // ── Render ────────────────────────────────────────────────────────────────
    const outerStyle = embedded
        ? { width: "100%" }
        : {
            width: "100%", display: "flex", justifyContent: "center", alignItems: "flex-start",
            background: "linear-gradient(135deg, #021a30 0%, #032B4E 30%, #032B4E 70%, #021a30 100%)",
            padding: "40px 0", minHeight: "100vh", boxSizing: "border-box",
            fontFamily: "Inter, sans-serif",
        };
    const cardStyle = embedded
        ? { width: "100%", display: "flex", flexDirection: "column", gap: 12 }
        : {
            width: "98%", maxWidth: 1600, margin: "0 auto", background: "#fff",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)", border: "1px solid #475C7E",
            borderRadius: 8, overflow: "visible", display: "flex", flexDirection: "column",
        };

    const filterSidebar = (
        <aside
            style={{
                ...(embedded ? ST.card : {}),
                width: embedded ? 280 : 300,
                flexShrink: 0,
                borderRight: embedded ? undefined : "1px solid #e5e7eb",
                background: embedded ? "#fff" : "#fafbfc",
                padding: embedded ? "14px 16px 18px" : "20px 20px 28px",
                boxSizing: "border-box",
            }}
        >
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <SlidersHorizontal size={15} color="#032B4E" />
                <span style={{ ...ST.bigTitle, flex: 1 }}>Dynamic Filters</span>
                {filtersLoading && filtersLoadedOnce && (
                    <span style={{ display: "inline-flex", marginLeft: 2 }}><Loader size="small" /></span>
                )}
                {embedded && (
                    <button
                        type="button"
                        onClick={() => setFiltersOpen(false)}
                        title="Hide filters"
                        style={{
                            border: "1px solid #e6eef4",
                            background: "#fff",
                            borderRadius: 6,
                            width: 28,
                            height: 28,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "#0c3c54",
                            flexShrink: 0,
                        }}
                    >
                        <ChevronRight size={14} />
                    </button>
                )}
            </div>

            <button
                onClick={resetAllFilters}
                title="Reset all filters to defaults"
                style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
                    background: "#fff", border: "1px solid #E06639", borderRadius: 8,
                    padding: "8px 0", fontSize: 12, fontWeight: 600, color: "#E06639",
                    cursor: "pointer", marginBottom: 4, fontFamily: "Poppins, sans-serif",
                }}
            >
                <RotateCcw size={13} /> Reset Filters
            </button>

            {!filtersLoadedOnce ? (
                <div style={{ marginTop: 16 }}>
                    {["Product Type", "Geographic Granularity", "Financial Year", "Segment", "Product Category"].map((lbl) => (
                        <div key={lbl} style={{ marginBottom: 18 }}>
                            <div style={{
                                height: 10, width: "45%", borderRadius: 4, marginBottom: 8,
                                background: "#e5e7eb", animation: "conjointPulse 1.2s ease-in-out infinite",
                            }} />
                            <div style={{
                                height: 36, width: "100%", borderRadius: 6,
                                background: "#eef1f4", animation: "conjointPulse 1.2s ease-in-out infinite",
                            }} />
                        </div>
                    ))}
                    <style>{`@keyframes conjointPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.45; } }`}</style>
                </div>
            ) : (
                <div style={{ opacity: filtersLoading ? 0.6 : 1, transition: "opacity 0.15s ease" }}>
                    <label style={ST.label}>Product Type</label>
                    <MultiSelectDropdown
                        singleSelect={true}
                        options={options.datasets} value={dataset} disabled={filtersLoading}
                        onChange={(v) => handleFilterChange("dataset", v)}
                    />

                    <label style={ST.label}>Geographic Granularity</label>
                    <MultiSelectDropdown
                        singleSelect={true}
                        options={options.region_levels} value={regionLevel} disabled={filtersLoading}
                        onChange={(v) => handleFilterChange("regionLevel", v)}
                    />

                    <label style={ST.label}>Financial Year</label>
                    <div
                        title="Locked to the latest available financial year"
                        style={{
                            width: "100%", padding: "9px 12px", borderRadius: 6,
                            border: "1px solid #e6eef4", background: "#f6fafd",
                            boxSizing: "border-box", display: "flex", alignItems: "center",
                            justifyContent: "space-between", gap: 8,
                        }}
                    >
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#0c3c54" }}>
                            {financialYear[0] || "—"}
                        </span>
                        <span style={{
                            fontSize: 10.5, fontWeight: 600, color: "#6a7d8c",
                            textTransform: "uppercase", letterSpacing: 0.4,
                            background: "#eef3f7", borderRadius: 4, padding: "2px 6px",
                        }}>
                            Latest
                        </span>
                    </div>

                    {isZonal && (
                        <>
                            <label style={ST.label}>Zone</label>
                            <MultiSelectDropdown
                                singleSelect={true}
                                options={options.zones} value={zone} disabled={filtersLoading}
                                onChange={(v) => handleFilterChange("zone", v)}
                            />
                        </>
                    )}

                    <label style={ST.label}>Segment</label>
                    <MultiSelectDropdown
                        singleSelect={true}
                        options={options.segments} value={segment} disabled={filtersLoading}
                        onChange={(v) => handleFilterChange("segment", v)}
                    />

                    <label style={ST.label}>Product Category</label>
                    <MultiSelectDropdown
                        singleSelect={true}
                        options={options.product_categories} value={category} disabled={filtersLoading}
                        onChange={(v) => handleFilterChange("category", v)}
                    />
                </div>
            )}
        </aside>
    );

    return (
        <div style={outerStyle}>
            <div style={cardStyle}>
                {/* ── Header (same gradient banner as workflow pages) ── */}
                {!embedded && (
                    <div style={{
                        background: "linear-gradient(135deg, #021a30 0%, #032B4E 50%, #021a30 100%)",
                        padding: "20px 24px", display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}>
                        <div>
                            <h2 style={{ color: "#fff", fontSize: 20, fontWeight: 700, margin: 0, fontFamily: "Poppins, sans-serif" }}>
                                New Product Development{datasetLabel ? ` — ${datasetLabel}` : ""}
                            </h2>
                            <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 13, margin: "4px 0 0" }}>
                                {filtersComplete
                                    ? [summarize(regionLevel), summarize(zone), summarize(segment), summarize(category)].filter(Boolean).join("  ›  ")
                                    + (comboCount > 1 ? `  ·  averaged across ${comboCount} combinations` : "")
                                    : "Feature importance, part-worth utilities and product preference simulation"}
                            </p>
                        </div>
                    </div>
                )}

                {/* ── Body ── */}
                <div style={{ display: "flex", alignItems: "stretch", gap: embedded ? 12 : 0 }}>
                    {(!embedded || (embedded && filtersOpen)) && filterSidebar}
                    
                    {embedded && !filtersOpen && (
                        <button
                            type="button"
                            onClick={() => setFiltersOpen(true)}
                            title="Show filters"
                            style={{
                                alignSelf: "flex-start",
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 6,
                                border: "1px solid #e6eef4",
                                background: "#fff",
                                borderRadius: 8,
                                padding: "8px 12px",
                                fontSize: 12,
                                fontWeight: 600,
                                color: "#0c3c54",
                                cursor: "pointer",
                                fontFamily: "Poppins, sans-serif",
                                boxShadow: "0 2px 8px rgba(12, 60, 84, 0.06)",
                                flexShrink: 0,
                            }}
                        >
                            <ChevronRight size={14} />
                            Filters
                        </button>
                    )}

                    <div style={{ flex: 1, minWidth: 0, padding: embedded ? 0 : "20px 24px 48px", background: embedded ? "transparent" : "#fff" }}>
                        {dashLoading ? (
                            <div style={ST.center}>
                                <Loader size="large" />
                                <p style={{ marginTop: 16, fontSize: 14, fontWeight: 600, color: "#032B4E" }}>
                                    Loading conjoint analysis…
                                </p>
                            </div>
                        ) : dashError ? (
                            <div style={ST.center}>
                                <div style={{ fontSize: 44 }}>⚠️</div>
                                <p style={{ fontSize: 16, fontWeight: 700, color: "#b91c1c", marginTop: 8 }}>Unable to load data</p>
                                <p style={{ fontSize: 14, color: "#6B7280", marginTop: 6 }}>{dashError}</p>
                            </div>
                        ) : !filtersComplete ? (
                            <div style={ST.center}>
                                <div style={{
                                    width: 64, height: 64, borderRadius: 16, background: "#f3f4f6",
                                    display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16,
                                }}>
                                    <SlidersHorizontal size={28} color="#9ca3af" />
                                </div>
                                <h3 style={{ ...ST.bigTitle, marginBottom: 4 }}>
                                    Select filters to begin
                                </h3>
                                <p style={{ ...ST.subtitle, lineHeight: 1.5, maxWidth: 320 }}>
                                    Choose a value for each filter in the panel on the left to generate the dashboard.
                                </p>
                            </div>
                        ) : (
                            <>
                                {/* ── KPI cards (Report4 style) ── */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 16, marginBottom: 24 }}>
                                    {[
                                        { label: "Importance", value: bestFeature ? `${fmtNum(bestFeature.relative_importance_pct)}%` : "-", accent: "#2EB8AA" },
                                        { label: "Optimal Utility", value: fmtNum(calcUtility(bestConfig)), accent: "#F2994A" },
                                        { label: "Features", value: allFeatures.length, accent: "#7a35c8" },
                                    ].map(({ label, value, accent }) => (
                                        <div key={label} style={{ ...ST.card, padding: "18px 20px", borderTop: `4px solid ${accent}` }}>
                                            <div style={{ fontSize: 12, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                {label}
                                            </div>
                                            <div style={{
                                                fontSize: typeof value === "string" && value.length > 14 ? 18 : 26,
                                                fontWeight: 800, color: "#032B4E", marginTop: 6,
                                                fontFamily: "Poppins, sans-serif", overflow: "hidden",
                                                textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            }}>
                                                {value}
                                            </div>
                                        </div>
                                    ))}
                                </div>


                                {/* ── Charts ── */}
                                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 20, marginBottom: 28 }}>
                                    {/* Relative importance donut */}
                                    <div style={{ ...ST.card, padding: "16px 20px" }}>
                                        <div style={ST.secTitle}>Relative Feature Importance</div>
                                        <div style={{ height: 320 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <PieChart>
                                                    <Pie
                                                        data={pieData}
                                                        cx="50%" cy="44%"
                                                        innerRadius="42%" outerRadius="68%"
                                                        paddingAngle={0}
                                                        dataKey="value" nameKey="name"
                                                        isAnimationActive={false}
                                                        label={({ value }) => `${fmtNum(value)}%`}
                                                        labelLine
                                                    >
                                                        {pieData.map((entry, i) => (
                                                            <Cell key={entry.name} fill={PALETTE[i % PALETTE.length]} stroke="none" strokeWidth={0} />
                                                        ))}
                                                    </Pie>
                                                    <ReTooltip content={<PieTooltipBox />} />
                                                    <Legend
                                                        iconType="circle" iconSize={10}
                                                        formatter={(value) => <span style={{ fontSize: 12, color: "#374151" }}>{value}</span>}
                                                    />
                                                </PieChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>

                                    {/* Part-worth utilities bar */}
                                    <div style={{ ...ST.card, padding: "16px 20px", display: "flex", flexDirection: "column" }}>
                                        <div style={ST.secTitle}>Part-Worth Utilities</div>
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                                            {allFeatures.map((f) => {
                                                const active = selectedChartFeature === f;
                                                return (
                                                    <button
                                                        key={f}
                                                        onClick={() => setSelectedChartFeature(f)}
                                                        style={{
                                                            padding: "6px 14px", borderRadius: 6, fontSize: 12.5, fontWeight: 700,
                                                            cursor: "pointer", transition: "all 0.2s ease",
                                                            border: active ? "2px solid #f79658" : "1px solid #d1d5db",
                                                            background: active ? "#f79658" : "#fff",
                                                            color: active ? "#fff" : "#374151",
                                                        }}
                                                    >
                                                        {f}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <div style={{ flex: 1, minHeight: 260 }}>
                                            <ResponsiveContainer width="100%" height="100%">
                                                <BarChart data={barData} margin={{ top: 10, right: 16, left: 0, bottom: 40 }}>
                                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                                                    <XAxis
                                                        dataKey="name" angle={-25} textAnchor="end" height={56}
                                                        tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false}
                                                        axisLine={{ stroke: "#d1d5db" }}
                                                    />
                                                    <YAxis
                                                        tick={{ fontSize: 12, fill: "#64748b" }} tickLine={false}
                                                        axisLine={{ stroke: "#d1d5db" }}
                                                    />
                                                    <ReferenceLine y={0} stroke="#94a3b8" />
                                                    <ReTooltip content={<BarTooltipBox />} cursor={{ fill: "rgba(3,43,78,0.04)" }} />
                                                    <Bar dataKey="partWorth" radius={[4, 4, 0, 0]} barSize={38} isAnimationActive={false}>
                                                        {barData.map((entry) => (
                                                            <Cell key={entry.name} fill={entry.partWorth >= 0 ? "#2EB8AA" : "#e74c3c"} />
                                                        ))}
                                                    </Bar>
                                                </BarChart>
                                            </ResponsiveContainer>
                                        </div>
                                    </div>
                                </div>


                                {/* ── Best configuration ── */}
                                <div style={{ marginBottom: 24 }}>
                                    <div style={ST.secTitle}>Product Feature Importance</div>
                                    <div style={{ ...ST.card, padding: "16px 20px" }}>
                                        <p style={{ ...ST.subtitle, margin: "0 0 12px" }}>
                                            For <strong style={{ color: "#032B4E" }}>{category.join(", ")}</strong>, these feature levels carry the highest utility
                                            {comboCount > 1 ? ` (averaged across ${comboCount} selected combinations)` : ""}:
                                        </p>
                                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
                                            {Object.entries(bestConfig).map(([feat, lvl]) => (
                                                <div key={feat} style={{
                                                    background: "#fdf1ec", border: "1px solid #f8d9c4",
                                                    borderRadius: 8, padding: "8px 14px", fontSize: 13,
                                                }}>
                                                    <span style={{ color: "#6B7280", fontWeight: 600, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.4 }}>
                                                        {feat}
                                                    </span>
                                                    <span style={{ color: "#032B4E", fontWeight: 700, marginLeft: 8 }}>{lvl}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>


                                {/* ── Product Comparison Simulator ── */}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
                                    <div>
                                        <div style={ST.bigTitle}>New Product Development Simulator</div>
                                        <div style={{ ...ST.subtitle, marginTop: 2 }}>
                                            Configure up to 5 hypothetical products and compare their predicted preference.
                                        </div>
                                    </div>
                                    <button
                                        onClick={addProduct}
                                        disabled={products.length >= 5}
                                        title={products.length >= 5 ? "Maximum of 5 products reached" : "Add Product"}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            background: products.length >= 5 ? "#d1d5db" : "#f79658",
                                            color: products.length >= 5 ? "#9ca3af" : "#fff",
                                            border: "none", borderRadius: 6,
                                            padding: "9px 18px", fontSize: 13, fontWeight: 700,
                                            cursor: products.length >= 5 ? "not-allowed" : "pointer",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        <Plus size={15} /> Add Product
                                    </button>
                                </div>

                                <div style={{ display: "flex", gap: 16, overflowX: "auto", paddingBottom: 16 }}>
                                    {/* Optimal product (read-only) */}
                                    <div style={{
                                        flex: "0 0 250px", ...ST.card, border: "2px solid #f79658",
                                        padding: "16px 18px", display: "flex", flexDirection: "column",
                                    }}>
                                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6, marginBottom: 14 }}>
                                            <Trophy size={15} color="#f79658" />
                                            <span style={{ fontSize: 14, fontWeight: 800, color: "#032B4E", fontFamily: "Poppins, sans-serif" }}>
                                                Optimal Product
                                            </span>
                                        </div>
                                        <div style={{ flex: 1 }}>
                                            {allFeatures.map((feat) => (
                                                <div key={feat} style={{ marginBottom: 12 }}>
                                                    <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                                                        {feat}
                                                    </div>
                                                    <div style={{
                                                        padding: "8px 12px", background: "#fdf1ec", border: "1px solid #f8d9c4",
                                                        borderRadius: 6, fontSize: 13, color: "#032B4E", fontWeight: 600,
                                                    }}>
                                                        {bestConfig[feat]}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #e5e7eb", textAlign: "center" }}>
                                            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                Utility Score
                                            </div>
                                            <div style={{ fontSize: 24, fontWeight: 800, color: "#032B4E", fontFamily: "Poppins, sans-serif" }}>
                                                {fmtNum(calcUtility(bestConfig))}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Custom products */}
                                    {products.map((product) => (
                                        <div key={product.id} style={{
                                            flex: "0 0 250px", ...ST.card,
                                            padding: "16px 18px", display: "flex", flexDirection: "column", position: "relative",
                                        }}>
                                            <button
                                                onClick={() => removeProduct(product.id)}
                                                title="Remove product"
                                                style={{
                                                    position: "absolute", top: 10, right: 10,
                                                    background: "#f3f4f6", border: "none", borderRadius: 6,
                                                    width: 24, height: 24, display: "flex", alignItems: "center", justifyContent: "center",
                                                    cursor: "pointer", color: "#6b7280", padding: 0,
                                                }}
                                            >
                                                <X size={13} />
                                            </button>
                                            <div style={{ fontSize: 14, fontWeight: 700, color: "#032B4E", fontFamily: "Poppins, sans-serif", textAlign: "center", marginBottom: 14 }}>
                                                {product.name}
                                            </div>
                                            <div style={{ flex: 1 }}>
                                                {allFeatures.map((feat) => (
                                                    <div key={feat} style={{ marginBottom: 12 }}>
                                                        <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 4 }}>
                                                            {feat}
                                                        </div>
                                                        <select
                                                            style={{ ...ST.select, padding: "8px 10px" }}
                                                            value={product.config[feat] || ""}
                                                            onChange={(e) => updateProduct(product.id, feat, e.target.value)}
                                                        >
                                                            {(featureLevels[feat] || []).map((lvl) => (
                                                                <option key={lvl} value={lvl}>{lvl}</option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ))}
                                            </div>
                                            <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed #e5e7eb", textAlign: "center" }}>
                                                <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                    Utility Score
                                                </div>
                                                <div style={{ fontSize: 24, fontWeight: 800, color: "#032B4E", fontFamily: "Poppins, sans-serif" }}>
                                                    {fmtNum(calcUtility(product.config))}
                                                </div>
                                            </div>
                                        </div>
                                    ))}

                                    {/* Add product tile */}
                                    <button
                                        onClick={addProduct}
                                        style={{
                                            flex: "0 0 250px", minHeight: 220, background: "#fafbfc",
                                            border: "2px dashed #d1d5db", borderRadius: 10, cursor: "pointer",
                                            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                                            gap: 8, color: "#6B7280",
                                        }}
                                    >
                                        <div style={{
                                            width: 40, height: 40, borderRadius: "50%", background: "#eef2f6",
                                            display: "flex", alignItems: "center", justifyContent: "center",
                                        }}>
                                            <Plus size={20} color="#032B4E" />
                                        </div>
                                        <span style={{ fontSize: 13, fontWeight: 600 }}>Add Product</span>
                                    </button>
                                </div>

                                {/* ── Customer preference ranking ── */}
                                <div style={{ marginTop: 24 }}>
                                    <div style={ST.secTitle}>Customer Preference Ranking</div>

                                    {/* Order strip */}
                                    <div style={{
                                        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8,
                                        background: "#f3f7fb", border: "1px solid #d6e4f0", borderRadius: 8,
                                        padding: "12px 16px", marginBottom: 14, fontSize: 13, color: "#334155",
                                    }}>
                                        <span style={{ fontWeight: 600, marginRight: 4 }}>Expected preference order:</span>
                                        {ranking.map((p, idx) => (
                                            <React.Fragment key={p.name}>
                                                {idx > 0 && <span style={{ color: "#94a3b8" }}>›</span>}
                                                <span style={{
                                                    background: idx === 0 ? "#fdf1ec" : "#fff",
                                                    border: `1px solid ${idx === 0 ? "#f8d9c4" : "#e2e8f0"}`,
                                                    borderRadius: 14, padding: "3px 12px",
                                                    fontWeight: idx === 0 ? 700 : 500,
                                                    color: idx === 0 ? "#b45309" : "#374151",
                                                }}>
                                                    {p.name}
                                                </span>
                                            </React.Fragment>
                                        ))}
                                    </div>

                                    {/* Ranking table */}
                                    <div style={ST.wrap}>
                                        <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "Inter, sans-serif", background: "#fff" }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ ...ST.th, width: 70 }}>Rank</th>
                                                    <th style={ST.th}>Product</th>
                                                    <th style={{ ...ST.th, textAlign: "center", width: 130 }}>Utility</th>
                                                    {/* <th style={{ ...ST.th, width: 280 }}>Preference Share</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {ranking.map((p, idx) => (
                                                    <tr key={p.name} style={{
                                                        borderBottom: "1px solid #e6e6e6",
                                                        background: idx === 0 ? "#fffaf7" : "#fff",
                                                    }}>
                                                        <td style={{ padding: "10px 12px", fontSize: 13, fontWeight: 700, color: "#032B4E" }}>
                                                            {idx === 0 ? <Trophy size={14} color="#f79658" style={{ verticalAlign: "-2px", marginRight: 4 }} /> : null}
                                                            {idx + 1}
                                                        </td>
                                                        <td style={{ padding: "10px 12px", fontSize: 13, color: "#1f2937", fontWeight: p.isOptimal ? 700 : 500 }}>
                                                            {p.name}
                                                        </td>
                                                        <td style={{ padding: "10px 12px", fontSize: 13, color: "#222", textAlign: "center", fontWeight: 600 }}>
                                                            {fmtNum(p.utility)}
                                                        </td>
                                                        {/* <td style={{ padding: "10px 12px" }}>
                                                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                                                <div style={{ flex: 1, height: 8, background: "#eef2f6", borderRadius: 4, overflow: "hidden" }}>
                                                                    <div style={{
                                                                        width: `${Math.min(100, Math.max(0, p.share))}%`, height: "100%",
                                                                        background: idx === 0 ? "#f79658" : "#2EB8AA", borderRadius: 4,
                                                                    }} />
                                                                </div>
                                                                <span style={{ fontSize: 13, fontWeight: 700, color: "#032B4E", width: 52, textAlign: "right" }}>
                                                                    {fmtNum(p.share)}%
                                                                </span>
                                                            </div>
                                                        </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
